/**
 * ActorDescriptor Enhanced Component
 * Unique Properties: Role hierarchy, permissions matrix, capabilities, delegation
 */

import { Avatar, Badge, Card, Checkbox, Container, Grid, Group, Paper, Stack, Tabs, Table, Text, Title } from '@mantine/core';
import '@mantine/core/styles.css';
import { IconShield, IconUser, IconUsers } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface ActorDescriptorProps {
    data?: any;
    mode?: 'view' | 'edit' | 'permissions';
}

export const ActorDescriptorEnhanced: React.FC<ActorDescriptorProps> = ({ data = {}, mode = 'view' }) => {
    const [activeTab, setActiveTab] = useState('actors');
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tableRef.current) {
            new TabulatorFull(tableRef.current, {
                data: [],
                layout: 'fitColumns',
                dataTree: true,
                dataTreeStartExpanded: false,
                columns: [
                    { title: 'ActorDescriptor', field: 'name' },
                    { title: 'Role', field: 'role' },
                    { title: 'Permissions', field: 'permissions', formatter: (cell: any) => {
                        const perms = cell.getValue() || [];
                        return perms.map((p: any) => `<span class="badge">${p}</span>`).join(' ');
                    }}
                ]
            });
        }
    }, []);

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Actor Descriptor</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="actors" leftSection={<IconUser size={16} />}>Actors</Tabs.Tab>
                        <Tabs.Tab value="roles" leftSection={<IconUsers size={16} />}>Roles</Tabs.Tab>
                        <Tabs.Tab value="permissions" leftSection={<IconShield size={16} />}>Permissions</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="actors">
                        <Stack mt="md">
                            <div ref={tableRef} />
                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group>
                                            <Avatar color="blue">AD</Avatar>
                                            <div>
                                                <Text fw={500}>Admin User</Text>
                                                <Text size="xs" color="dimmed">Super Admin</Text>
                                            </div>
                                        </Group>
                                        <Group gap="xs" mt="xs">
                                            <Badge size="sm">Create</Badge>
                                            <Badge size="sm">Read</Badge>
                                            <Badge size="sm">Update</Badge>
                                            <Badge size="sm">Delete</Badge>
                                        </Group>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="permissions">
                        <Card withBorder mt="md">
                            <Title order={5} mb="md">Permission Matrix</Title>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Resource</th>
                                        <th>Create</th>
                                        <th>Read</th>
                                        <th>Update</th>
                                        <th>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Workflows</td>
                                        <td><Checkbox defaultChecked /></td>
                                        <td><Checkbox defaultChecked /></td>
                                        <td><Checkbox defaultChecked /></td>
                                        <td><Checkbox /></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default ActorDescriptorEnhanced;
